import { useActiveOrgId } from '@/hooks/useActiveOrgId'
import { useGetOrgUserProfile } from '@/services/auth'
import { useGetOrgSpendRate, useGetOrgUsageGraph } from '@/services/home'
import { formatCurrency } from '@/lib/utils'
import { getFormattedUnixDate } from '@/helpers/time'
import { useMemo } from 'react'
import { DateRange } from 'react-day-picker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useListServerInstances } from '@/services/server'
dayjs.extend(utc)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export type UsageStats = {
  periodSpend: string
  rollingAverageRate: string
  spendRate: string
  isLoading: boolean
}

// Type for the usage data items
type UsageDataItem = {
  total_cost: number
  formatted_total_cost: string
}

export const useUsageChartData = (range: DateRange, userId?: number) => {
  const { orgId } = useActiveOrgId()
  const { data: org, isLoading: isOrgUserProfileLoading } =
    useGetOrgUserProfile(orgId)

  // Check if user has server instances
  const { data: serverData, isLoading: isServerLoading } =
    useListServerInstances({
      showAllUsers: userId ? false : true,
    })

  const totalDaysDiff = dayjs(range.to).diff(dayjs(range.from), 'day')

  const { data: spentRate, isLoading: isOrgSpendRateLoading } =
    useGetOrgSpendRate(userId)

  const { data, isLoading: isUsageGraphLoading } = useGetOrgUsageGraph(
    {
      start_at: getFormattedUnixDate(range.from, { time: 'startOfDay' }),
      end_at: getFormattedUnixDate(range.to, { time: 'endOfDay' }),
    },
    userId
  )

  const chartData = useMemo(() => {
    if (!range.from || !range.to) return []

    // Generate all dates in the date range
    const allDates: string[] = []
    let currentDate = dayjs(range.from)
    const endDate = dayjs(range.to)

    // Safety counter to prevent infinite loops
    let safetyCounter = 0
    const MAX_ITERATIONS = 366 // Maximum days in a year + 1 for leap years

    while (
      currentDate.isSameOrBefore(endDate, 'day') &&
      safetyCounter < MAX_ITERATIONS
    ) {
      allDates.push(currentDate.format('YYYY-MM-DD'))
      currentDate = currentDate.add(1, 'day')
      safetyCounter++
    }

    // Create a map of dates to usage data
    const usageDataMap: Record<string, UsageDataItem> = {}

    if (data?.items) {
      data.items.forEach((item) => {
        const itemDate = dayjs.utc(item.date).format('YYYY-MM-DD')
        usageDataMap[itemDate] = {
          total_cost: item.total_cost / 100,
          formatted_total_cost: formatCurrency(item.total_cost / 100),
        }
      })
    }

    // Generate chart data for all dates, with zero values for dates without usage data
    return allDates.map((dateStr) => {
      const displayDate = dayjs(dateStr).format('D MMM')

      if (usageDataMap[dateStr]) {
        return {
          date: displayDate,
          ...usageDataMap[dateStr],
        }
      } else {
        return {
          date: displayDate,
          total_cost: 0,
          formatted_total_cost: formatCurrency(0),
        }
      }
    })
  }, [data, range])

  const stats = useMemo((): UsageStats => {
    const formattedSpendRate = !spentRate
      ? '$0.00'
      : formatCurrency(spentRate?.summary?.total_cost / 100)

    if (chartData.length === 0) {
      return {
        periodSpend: '$0.00',
        rollingAverageRate: '$0.00',
        spendRate: formattedSpendRate,
        isLoading: isOrgSpendRateLoading,
      }
    }

    const totalCost = chartData.reduce((acc, item) => acc + item.total_cost, 0)

    const periodSpend = formatCurrency(totalCost)
    const rollingAverageRate = formatCurrency(totalCost / chartData.length)

    return {
      periodSpend,
      rollingAverageRate,
      spendRate: formattedSpendRate,
      isLoading: isOrgSpendRateLoading,
    }
  }, [chartData, spentRate, isOrgSpendRateLoading])

  const isDateDisabled = (date: Date) => {
    return isOrgUserProfileLoading
      ? true
      : dayjs(date).isBefore(dayjs(org?.org.created_at))
  }

  return {
    data: chartData,
    isLoading:
      isUsageGraphLoading ||
      isServerLoading ||
      isOrgSpendRateLoading ||
      isOrgUserProfileLoading,
    totalDaysDiff,
    isDateDisabled,
    isEmpty: chartData.length === 0,
    hasNoServers: !(
      serverData?.allInstances && serverData.allInstances.length > 0
    ),
    stats,
  }
}
